import { useMutation, useQueryClient } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

/**
 * Polls the vector stores list until the newly created store appears
 * Uses exponential backoff with a maximum of 10 attempts
 */

const pollForVectorStore = async ({
  qc,
  storeId,
  auth,
  workspaceId,
  attempt = 0,
  maxAttempts = 10,
}: {
  qc: ReturnType<typeof useQueryClient>;
  storeId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  auth: any;
  workspaceId: number;
  attempt?: number;
  maxAttempts?: number;
}) => {
  if (attempt >= maxAttempts) {
    // eslint-disable-next-line no-console
    console.warn(`Max polling attempts reached for vector store ${storeId}`);
    return;
  }

  const delay = Math.min(500 * Math.pow(2, attempt), 10000);

  // eslint-disable-next-line no-promise-executor-return
  await new Promise(resolve => setTimeout(resolve, delay));

  try {
    const response = await fetch(
      `${API_URL}/api/dashboard/v2/vector-store/list?workspace_id=${workspaceId}`,
      {
        method: "GET",
        headers: jsonAuthHeaders(auth?.userToken ?? ""),
      },
    );

    const result = await handleResponse(response);
    const storeExists = result?.data?.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (store: any) => store.id === storeId,
    );

    if (storeExists) {
      await qc.invalidateQueries({ queryKey: ["vector-stores", workspaceId] });
      return;
    }

    return pollForVectorStore({
      qc,
      storeId,
      auth,
      workspaceId,
      attempt: attempt + 1,
      maxAttempts,
    });
  } catch {
    return pollForVectorStore({
      qc,
      storeId,
      auth,
      workspaceId,
      attempt: attempt + 1,
      maxAttempts,
    });
  }
};

/**
 * Create a vector store
 */
export const useCreateVectorStore = () => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;
  const qc = useQueryClient();

  return useMutation(
    async (body: {
      name: string;
      file_ids?: string[];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      expires_after?: any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metadata?: any;
    }) => {
      const response = await fetch(`${API_URL}/api/dashboard/v2/vector-store/create`, {
        method: "POST",
        headers: jsonAuthHeaders(auth!.userToken!),
        body: JSON.stringify({ ...body, workspace_id: workspaceId }),
      }).then(handleResponse);

      // Check if response has success: false
      if (response && typeof response === "object" && "success" in response) {
        if (response.success === false) {
          const errorMessage =
            (response as { message?: string }).message || "Failed to create vector store";
          throw new Error(errorMessage);
        }
      }

      return response;
    },
    {
      onSuccess: data => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        qc.setQueryData(["vector-stores", workspaceId], (old: any) => {
          if (!old) {
            return { data: [data.vector_store] };
          }
          return {
            ...old,
            data: [data.vector_store, ...(old.data || [])],
          };
        });

        if (data?.vector_store?.id && workspaceId) {
          pollForVectorStore({ qc, storeId: data.vector_store.id, auth, workspaceId });
        }
      },
    },
  );
};
